import {
  type AccessLevel,
  type AccessMatrixResponse,
  getAccessMatrix,
  levelFromFlags,
  updateAccessMatrix,
} from '@lms/api-client/backend';
import { Button } from '@lms/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@lms/ui/card';
import { Input } from '@lms/ui/input';
import { Label } from '@lms/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@lms/ui/select';
import { useToast } from '@lms/ui/use-toast';
import { cn } from '@lms/ui/utils';
import { LoaderCircle } from 'lucide-react';
import * as React from 'react';
import useSWR, { useSWRConfig } from 'swr';
import { useTranslations } from 'use-intl';
import { GroupSelector } from '@/features/student/components/group-selector';
import { accessMatrixKey } from './access-matrix-section';
import { commonLevel, LEVEL_META, LEVELS } from './levels';

// Step shown right after a location is created. Access is explicit per
// location, so a new location is visible to super-admins only until it's
// shared. People with access to every other location in the portfolio are
// pre-selected at the access they have everywhere; "copy from" replaces the
// selection with another location's access list.
export function ShareLocationAccess({
  portfolioId,
  location,
  onDone,
}: {
  portfolioId: number;
  location: { id: number; name: string };
  onDone: () => void;
}) {
  const t = useTranslations('access_matrix');
  const { data, isLoading } = useSWR(accessMatrixKey(portfolioId), () =>
    getAccessMatrix(portfolioId),
  );

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('share_title', { location: location.name })}</CardTitle>
        <p className="text-muted-foreground text-sm">{t('share_intro')}</p>
      </CardHeader>
      <CardContent>
        {isLoading || !data ? (
          <div className="flex items-center justify-center py-12">
            <LoaderCircle className="text-primary size-8 animate-spin" />
          </div>
        ) : (
          <ShareForm
            data={data}
            portfolioId={portfolioId}
            locationId={location.id}
            onDone={onDone}
          />
        )}
      </CardContent>
    </Card>
  );
}

function ShareForm({
  data,
  portfolioId,
  locationId,
  onDone,
}: {
  data: AccessMatrixResponse;
  portfolioId: number;
  locationId: number;
  onDone: () => void;
}) {
  const t = useTranslations('access_matrix');
  const { toast } = useToast();
  const { mutate } = useSWRConfig();

  const others = React.useMemo(
    () => data.locations.filter((l) => l.id !== locationId),
    [data.locations, locationId],
  );

  const levelAt = React.useMemo(() => {
    const m = new Map<string, AccessLevel>();
    for (const c of data.cells) {
      m.set(
        `${c.userId}:${c.locationId}`,
        levelFromFlags(c.canManageGroups, c.canManageUsers),
      );
    }
    return (userId: number, locId: number) =>
      m.get(`${userId}:${locId}`) ?? 'none';
  }, [data.cells]);

  const defaults = React.useMemo(() => {
    const m = new Map<number, AccessLevel>();
    if (others.length === 0) return m;
    for (const u of data.users) {
      const level = others.reduce<AccessLevel>(
        (acc, l) => commonLevel(acc, levelAt(u.id, l.id)),
        'all',
      );
      if (level !== 'none') m.set(u.id, level);
    }
    return m;
  }, [data.users, others, levelAt]);

  const [levels, setLevels] = React.useState(defaults);
  // Who sorts to the top: the selection as of the last source change, so rows
  // don't jump while you edit their levels.
  const [pinned, setPinned] = React.useState(() => new Set(defaults.keys()));
  const [copySource, setCopySource] = React.useState('');
  const [query, setQuery] = React.useState('');
  const [saving, setSaving] = React.useState(false);

  const onCopyFrom = (value: string) => {
    setCopySource(value);
    if (!value) {
      setLevels(defaults);
      setPinned(new Set(defaults.keys()));
      return;
    }
    const source = Number(value);
    const m = new Map<number, AccessLevel>();
    for (const u of data.users) {
      const level = levelAt(u.id, source);
      if (level !== 'none') m.set(u.id, level);
    }
    setLevels(m);
    setPinned(new Set(m.keys()));
  };

  const setLevel = (userId: number, level: AccessLevel) =>
    setLevels((prev) => {
      const next = new Map(prev);
      if (level === 'none') next.delete(userId);
      else next.set(userId, level);
      return next;
    });

  // Locked users can't be granted by this caller; the backend would reject the
  // whole batch, so leave them out.
  const users = React.useMemo(() => {
    const q = query.trim().toLowerCase();
    return data.users
      .filter((u) => !u.locked)
      .filter(
        (u) =>
          !q ||
          u.name.toLowerCase().includes(q) ||
          u.email.toLowerCase().includes(q),
      )
      .sort(
        (a, b) =>
          Number(pinned.has(b.id)) - Number(pinned.has(a.id)) ||
          a.name.localeCompare(b.name),
      );
  }, [data.users, query, pinned]);

  const lockedIds = React.useMemo(
    () => new Set(data.users.filter((u) => u.locked).map((u) => u.id)),
    [data.users],
  );
  const grants = [...levels].filter(([userId]) => !lockedIds.has(userId));

  const save = async () => {
    setSaving(true);
    try {
      const fresh = await updateAccessMatrix(
        portfolioId,
        grants.map(([userId, level]) => ({ userId, locationId, level })),
      );
      await mutate(accessMatrixKey(portfolioId), fresh, {
        revalidate: false,
      });
      toast({
        title: t('shared', { count: grants.length }),
      });
      onDone();
    } catch (err) {
      toast({
        variant: 'destructive',
        title: t('share_failed'),
        description: err instanceof Error ? err.message : String(err),
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-2">
        <Label>{t('copy_from_label')}</Label>
        <GroupSelector
          groups={others.map((l) => ({ value: `${l.id}`, label: l.name }))}
          placeholder={t('pick_location')}
          emptyString={t('no_location')}
          value={copySource}
          onChange={onCopyFrom}
        />
        <p className="text-muted-foreground text-sm">
          {copySource ? t('share_copy_help') : t('share_suggest_help')}
        </p>
      </div>

      <Input
        placeholder={t('filter_people')}
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        className="h-9 w-64"
      />

      <div className="max-h-[50vh] divide-y overflow-auto rounded-md border">
        {users.map((u) => {
          const level = levels.get(u.id) ?? 'none';
          return (
            <div
              key={u.id}
              className="flex items-center justify-between gap-4 px-3 py-2"
            >
              <div className="min-w-0">
                <div className="truncate text-sm">{u.name}</div>
                <div className="text-muted-foreground truncate text-xs">
                  {u.email}
                </div>
              </div>
              <Select
                value={level}
                onValueChange={(v) => setLevel(u.id, v as AccessLevel)}
              >
                <SelectTrigger className="h-8 w-44 shrink-0">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {LEVELS.map((lvl) => (
                    <SelectItem key={lvl} value={lvl}>
                      <span className="flex items-center gap-2">
                        <span
                          className={cn(
                            'inline-block h-3 w-3 rounded-sm',
                            LEVEL_META[lvl].swatch,
                          )}
                        />
                        {t(`level_${lvl}`)}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          );
        })}
        {users.length === 0 && (
          <p className="text-muted-foreground py-6 text-center text-sm">
            {t('no_people')}
          </p>
        )}
      </div>

      <div className="flex items-center gap-2">
        <span className="text-muted-foreground mr-auto text-sm">
          {t('share_count', { count: grants.length })}
        </span>
        <Button variant="outline" disabled={saving} onClick={onDone}>
          {t('skip')}
        </Button>
        <Button disabled={saving || grants.length === 0} onClick={save}>
          {saving && <LoaderCircle className="h-4 w-4 animate-spin" />}
          {t('share')}
        </Button>
      </div>
    </div>
  );
}
